/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState } from 'react';
import Select from 'react-select';
import { Field, FieldProps, FormikErrors } from 'formik';
import ClipLoader from 'react-spinners/ClipLoader';
import DrugLookup from './DrugLookup/DrugLookup.component';
import DrugLookupService from '../../services/DrugLookup/drugLookup.service';
import { DrugDetails, DrugLookupResponseDto, MappedDrugDetail } from '../../interfaces/DrugLookupResponseDto';
import FormRow from '../FormRow/formrow.component';

type SelectunitDropProps =
    {
      label: string,
      value: string
    };

    type SelectrouteDropProps =
    {
      label: string,
      value: string
    };

    type SelectfrequencyDropProps =
    {
      label: string,
      value: string
    };

type FormDataProps = {
  [key: string]: any,
  startDate: Date | null,
  unitType: SelectunitDropProps,
  unitFrequency: string;
  modifier: string;
  modifier2: string;
  NDCCode: string;
  drugName: string;
  doseQty: number | null;
  doseQtyUnit: string;
  route: SelectrouteDropProps;
  frequency: SelectfrequencyDropProps;
  adjustedDrugDosage : string | null
};

interface PharmacyProps {
  formData: FormDataProps;
  errors: FormikErrors<FormDataProps>;
  setFieldValue: (field: string, value: any) => void;
  ErrorMessage: React.FC<{ errors: FormikErrors<FormDataProps>; fieldName: string }>
  handleSelectedDrugItem: (value: any) => void;
}

const Pharmacy: React.FC<PharmacyProps> = ({ formData, setFieldValue, errors, ErrorMessage, handleSelectedDrugItem }) => {
  const [toggleDrugLookupState, setToggleDrugLookup] = useState(false);
  const [drugData, setDrugData] = useState<Array<MappedDrugDetail>>([]);
  const [medicationOrNdcCode, setMedicationOrNdcCode] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [lookUpDisable, setlookUpDisable] = useState(true);

  const frequencyOptions = [
    {
      label: 'As directed',
      value: 'As directed',
    },
    {
      label: 'Daily',
      value: 'Daily',
    },
    {
      label: 'Twice a day',
      value: 'Twice a day',
    },
    {
      label: 'Three times a day',
      value: 'Three times a day',
    },
    {
      label: 'Four times a day',
      value: 'Four times a day',
    },
  ];

  const routeOptions = [
    {
      label: 'Other',
      value: 'Other',
    },
    {
      label: 'Injected',
      value: 'Injected',
    },
    {
      label: 'Infused',
      value: 'Infused',
    }];

  const drugLookup = () => {
    if (medicationOrNdcCode !== '') {
      const isNumeric = /^[0-9]+$/.test(medicationOrNdcCode);
      if (isNumeric) {
        getDrugLookupDtoOnNdcCode(medicationOrNdcCode);
      } else {
        getDrugLookupDtoOnDrugName(medicationOrNdcCode);
      }
    } else {
      setError('Please enter NDC or drug name');
    }
  };

  const filterLookupData = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setError(null);
    setlookUpDisable(false);
    const isNumeric = /^[0-9]+$/.test(value);

    if (isNumeric) {
      if (value.length !== 9 && value.length !== 11) {
        setError('Number must be either 9 or 11 digits long');
        setlookUpDisable(true);
      }
    } else if (value.length < 3) {
      setError('Text must be more than 2 characters long');
      setlookUpDisable(true);
    } else if (/[0-9]/.test(value)) {
      setError('Text cannot contain characters');
      setlookUpDisable(true);
    }
    setMedicationOrNdcCode(value);
    setToggleDrugLookup(true);
  };

  const handleDrugLookupResponse = (result: DrugLookupResponseDto, noDataMessage: string) => {
    if (!result.isSuccess) {
      ShowErrorMessage('Please try again later. If the issue persists, please contact the system administrator.');
      return;
    }

    if (result.drugDetails?.length === 0) {
      ShowErrorMessage(noDataMessage);
      return;
    }

    setError(null);
    updateDrugsData(result.drugDetails || []);
  };

  const getDrugLookupDtoOnNdcCode = async (ndcCode: string) => {
    setLoading(true);
    const result: DrugLookupResponseDto = await DrugLookupService.getDrugLookupDtoOnNdcCode(ndcCode);
    handleDrugLookupResponse(result, 'No drug found for the provided code');
    setLoading(false);
  };

  const getDrugLookupDtoOnDrugName = async (drugName: string) => {
    setLoading(true);
    const result: DrugLookupResponseDto = await DrugLookupService.getDrugLookupDtoOnDrugName(drugName);
    handleDrugLookupResponse(result, 'No drug found for the provided name');
    setLoading(false);
  };

  const ShowErrorMessage = async (errorMessage: string) => {
    setError(errorMessage);
    updateDrugsData([]);
    setToggleDrugLookup(false);
  };

  const handleDrugSelection = (drugItem: any) => {
    handleSelectedDrugItem(drugItem);
  };

  const updateDrugsData = (drugSData: DrugDetails[]) => {
    const mappedData = drugSData.map((detail) => ({
      NDCCode: detail.drugIdentifiers?.ndc || 'N/A',
      drugName: detail.drugName?.brandName || 'N/A',
      doseQty: parseFloat(detail.dosage?.packageSizeQuantity || '0'),
      doseQtyUnit: 'MG',
    }));
    setDrugData(mappedData);
    setToggleDrugLookup(true);
  };

  return (
    <div className="position-relative">
      <div className={`${loading ? 'p-3 overlay-container' : 'form-group  medication-name'}`}>
        <label className="fw-bold mb-0" htmlFor="medication-name">
          Medication Name or NDC Code
          {' '}
          {drugData.length > 0 ? `(${drugData.length} Total)` : ''}
        </label>
        <div className="input-group">
          <Field
            type="text"
            id="medication-name"
            name="medication-name"
            required="false"
            render={({ field }: FieldProps) => (
              <input {...field} type="text" className="form-control" data-testid="medication-name" placeholder="Start typing to search..." onChange={filterLookupData} />
            )}
          />
          <span />
&nbsp;
          <button className="btn-outline-primary rounded btn-sm btnstyle" data-testid="lookup" type="button" onClick={() => drugLookup()} disabled={loading || lookUpDisable}>
            {loading ? 'Loading...' : 'Look Up'}
          </button>
        </div>
        {error && <div className="text-danger">{error}</div>}
      </div>
      {loading && (
        <div className="d-flex justify-content-center align-items-center position-absolute w-100 bg-light bg-opacity-75">
          <ClipLoader
            color="#00BFFF"
            loading={loading}
            size={40}
          />
        </div>
      )}
      {toggleDrugLookupState ? (
        <DrugLookup
          items={drugData}
          handleDrugSelection={handleDrugSelection}
        />
      ) : ''}
      <FormRow className="mt-3">
        <div className="form-group" aria-label="Frequency">
          <label className="fw-bold mb-0" htmlFor="frequency">Frequency</label>
          <Select
            id="frequency"
            name="frequency.value"
            options={frequencyOptions}
            defaultValue={formData.frequency}
            isSearchable={false}
            onChange={(option) => setFieldValue('frequency', option)}
            className={errors.frequency ? 'selectUnitTypeError' : 'selectUnitType'}
          />
          <ErrorMessage errors={errors} fieldName="frequency" />
        </div>
        <div className="form-group" aria-label="Route">
          <label className="fw-bold mb-0" htmlFor="route">Route</label>
          <Select
            id="route"
            name="route.value"
            options={routeOptions}
            defaultValue={formData.route}
            isSearchable={false}
            onChange={(option) => setFieldValue('route', option)}
            className={errors.route ? 'selectUnitTypeError' : 'selectUnitType'}
          />
          <ErrorMessage errors={errors} fieldName="route" />
        </div>
      </FormRow>
      <div>
        <label className="fw-bold mb-0 mt-3" htmlFor="drugUnitDescription">Adjusted Drug Dosage</label>
        <Field
          type="text"
          id="drugUnitDescription"
          name="adjustedDrugDosage"
          required="true"
          render={({ field }: FieldProps) => (
            <input
              {...field}
              type="text"
              className="form-control inputField"
              data-testid="drugUnitDescription"
            />
          )}
        />
      </div>
    </div>
  );
};

export default Pharmacy;
